import { Link } from "react-router-dom";
import { Wallet, BadgeCheck, ArrowUpRight, Calculator } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { localizedPath } from "../../lib/locale";

const TIERS = [
    { spend: "< $250k", white: 4, gray: 7, casino: 10 },
    { spend: "$250k – $500k", white: 3.5, gray: 6, casino: 10 },
    { spend: "$500k – $1M", white: 3, gray: 5, casino: 9 },
    { spend: "$1M – $2.5M", white: 2.5, gray: 4, casino: 8, cashback: "0.5–1%" },
    { spend: "$2.5M+", white: 2, gray: 3, casino: 7, cashback: "1–2%" },
];

const T = {
    tr: {
        title: (n) => `${n} — cüzdan yükleme koşulları`,
        sub: "Hesap ücreti yok, kurulum ücreti yok, aylık abonelik yok. Sadece cüzdanına yüklediğin tutar üzerinden hacme göre düşen bir komisyon ödersin.",
        callout: "Aşağıdaki tutar bize ödediğin bir ücret DEĞİL. Cüzdanına yüklediğin bu para senin reklam bütçendir; komisyon bu tutarın içinden düşülür, üstüne ekstra bir şey ödemezsin.",
        minLabel: "Minimum ilk yükleme", minNote: "Komisyon düşülür, kalanı reklam bakiyen olur",
        metaTypes: "Meta'da iki hesap tipi var",
        metaStd: "Standard — kart bağlı hesap (BM2500 · HIVA Platinum)",
        metaFlex: "Flex — Credit Line hesap (kart yok)",
        feeTitle: "Komisyon (yüklediğin tutar üzerinden, aylık harcamaya göre düşer)",
        spend: "Aylık harcama", white: "White-hat", gray: "Gray-hat", cashback: "Cashback",
        casino: "Casino & Gambling (lisanslı, tüm platformlar): %10 → %7",
        example: (m) => `Örnek: $${m.toLocaleString()} yüklersin → white-hat %4 komisyon ($${(m * 0.04).toFixed(2)}) düşülür → reklam hesabına $${(m * 0.96).toFixed(2)} geçer.`,
        perks: ["Aylık abonelik yok", "Hesap / kurulum ücreti yok", "Sınırsız hesap değişimi, bakiye aktarımı"],
        signup: "Ücretsiz hesap oluştur", calc: "Komisyon hesaplayıcı",
    },
    en: {
        title: (n) => `${n} — wallet top-up terms`,
        sub: "No account fee, no setup fee, no monthly subscription. You only pay a volume-based commission on what you load into your wallet.",
        callout: "The amount below is NOT a fee you pay us. The money you load is your own ad budget; the fee is deducted from inside this amount — nothing extra is charged on top.",
        minLabel: "Minimum first top-up", minNote: "Fee deducted, the rest becomes your ad balance",
        metaTypes: "Two account types on Meta",
        metaStd: "Standard — card-linked account (BM2500 · HIVA Platinum)",
        metaFlex: "Flex — Credit Line account (no card)",
        feeTitle: "Commission (charged on what you top up, drops with monthly spend)",
        spend: "Monthly spend", white: "White-hat", gray: "Gray-hat", cashback: "Cashback",
        casino: "Casino & Gambling (licensed, all platforms): 10% → 7%",
        example: (m) => `Example: load $${m.toLocaleString()} → 4% white-hat fee ($${(m * 0.04).toFixed(2)}) is deducted → $${(m * 0.96).toFixed(2)} lands in the ad account.`,
        perks: ["No monthly subscription", "No account or setup fee", "Unlimited replacements with balance transfer"],
        signup: "Create free account", calc: "Fee calculator",
    },
    es: {
        title: (n) => `${n} — condiciones de recarga de la wallet`,
        sub: "Sin cuota de cuenta, sin coste de configuración, sin suscripción mensual. Solo pagas una comisión por volumen sobre lo que cargas en tu wallet.",
        callout: "El importe de abajo NO es una tarifa que nos pagas. El dinero que cargas es tu propio presupuesto publicitario; la comisión se descuenta de dentro de este importe — no se cobra nada extra encima.",
        minLabel: "Recarga mínima inicial", minNote: "Se descuenta la comisión, el resto es tu saldo publicitario",
        metaTypes: "Dos tipos de cuenta en Meta",
        metaStd: "Standard — cuenta con tarjeta (BM2500 · HIVA Platinum)",
        metaFlex: "Flex — cuenta Credit Line (sin tarjeta)",
        feeTitle: "Comisión (sobre lo que recargas, baja con el gasto mensual)",
        spend: "Gasto mensual", white: "White-hat", gray: "Gray-hat", cashback: "Cashback",
        casino: "Casino & Gambling (con licencia, todas las plataformas): 10% → 7%",
        example: (m) => `Ejemplo: cargas $${m.toLocaleString()} → se descuenta la comisión white-hat del 4% ($${(m * 0.04).toFixed(2)}) → $${(m * 0.96).toFixed(2)} llegan a la cuenta publicitaria.`,
        perks: ["Sin suscripción mensual", "Sin cuota de cuenta ni configuración", "Reemplazos ilimitados con traspaso de saldo"],
        signup: "Crear cuenta gratis", calc: "Calculadora de comisiones",
    },
    pt: {
        title: (n) => `${n} — condições de recarga da wallet`,
        sub: "Sem taxa de conta, sem taxa de configuração, sem assinatura mensal. Você paga apenas uma comissão por volume sobre o que carrega na sua wallet.",
        callout: "O valor abaixo NÃO é uma taxa que você nos paga. O dinheiro que você carrega é o seu próprio orçamento de anúncios; a comissão é descontada de dentro desse valor — nada extra é cobrado por cima.",
        minLabel: "Recarga mínima inicial", minNote: "A comissão é descontada, o restante vira seu saldo de anúncios",
        metaTypes: "Dois tipos de conta na Meta",
        metaStd: "Standard — conta com cartão (BM2500 · HIVA Platinum)",
        metaFlex: "Flex — conta Credit Line (sem cartão)",
        feeTitle: "Comissão (sobre o que você recarrega, cai com o gasto mensal)",
        spend: "Gasto mensal", white: "White-hat", gray: "Gray-hat", cashback: "Cashback",
        casino: "Casino & Gambling (licenciado, todas as plataformas): 10% → 7%",
        example: (m) => `Exemplo: você carrega $${m.toLocaleString()} → a comissão white-hat de 4% ($${(m * 0.04).toFixed(2)}) é descontada → $${(m * 0.96).toFixed(2)} vão para a conta de anúncios.`,
        perks: ["Sem assinatura mensal", "Sem taxa de conta ou configuração", "Substituições ilimitadas com transferência de saldo"],
        signup: "Criar conta grátis", calc: "Calculadora de comissão",
    },
    ru: {
        title: (n) => `${n} — условия пополнения кошелька`,
        sub: "Без платы за аккаунт, без платы за настройку, без ежемесячной подписки. Вы платите только комиссию по объёму с суммы, которую вносите в кошелёк.",
        callout: "Сумма ниже — НЕ плата нам. Деньги, которые вы вносите, — это ваш собственный рекламный бюджет; комиссия вычитается из этой суммы — ничего сверху не списывается.",
        minLabel: "Минимальный первый депозит", minNote: "Комиссия вычитается, остаток становится вашим рекламным балансом",
        metaTypes: "Два типа аккаунтов в Meta",
        metaStd: "Standard — аккаунт с картой (BM2500 · HIVA Platinum)",
        metaFlex: "Flex — аккаунт Credit Line (без карты)",
        feeTitle: "Комиссия (с суммы пополнения, снижается с ростом месячного расхода)",
        spend: "Расход в месяц", white: "White-hat", gray: "Gray-hat", cashback: "Кэшбэк",
        casino: "Casino & Gambling (с лицензией, все платформы): 10% → 7%",
        example: (m) => `Пример: вносите $${m.toLocaleString()} → вычитается комиссия white-hat 4% ($${(m * 0.04).toFixed(2)}) → $${(m * 0.96).toFixed(2)} поступает на рекламный аккаунт.`,
        perks: ["Без ежемесячной подписки", "Без платы за аккаунт и настройку", "Неограниченные замены с переносом баланса"],
        signup: "Создать бесплатный аккаунт", calc: "Калькулятор комиссии",
    },
};

export default function PlatformTermsDialog({ platform, minTopup, minTopupFlex, language, onClose }) {
    const t = T[language] || T.en;
    const open = Boolean(platform);
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="platform-terms-dialog" className="max-w-lg bg-[#0d0d10] border-white/10 p-0 overflow-hidden">
                {platform && (
                    <>
                        <DialogHeader className="p-6 pb-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] grid place-items-center" style={{ color: platform.accent }}>
                                    <platform.Icon className="w-6 h-6" />
                                </div>
                                <DialogTitle className="font-display font-bold text-lg md:text-xl tracking-tight" data-testid="platform-terms-title">{t.title(platform.name)}</DialogTitle>
                            </div>
                            <DialogDescription className="text-sm text-muted-foreground">{t.sub}</DialogDescription>
                        </DialogHeader>

                        <div className="px-6 space-y-4">
                            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3 flex gap-3 text-sm" data-testid="platform-terms-callout">
                                <Wallet className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                <p className="text-emerald-100/90 leading-relaxed">{t.callout}</p>
                            </div>

                            <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] px-4 py-3 flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{t.minLabel}</p>
                                    <p className="text-xs text-emerald-400 mt-0.5">{t.minNote}</p>
                                </div>
                                <p className="font-display font-black text-2xl tabular-nums" data-testid="platform-terms-min-topup">${Number(minTopup).toLocaleString()}</p>
                            </div>
                            {platform.slug === "meta" && (
                                <div className="rounded-xl border border-white/[0.07] px-4 py-3 space-y-1.5" data-testid="platform-terms-meta-types">
                                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{t.metaTypes}</p>
                                    <div className="flex items-center justify-between text-xs"><span className="text-foreground/85">{t.metaStd}</span><span className="font-semibold tabular-nums">${Number(minTopup).toLocaleString()}</span></div>
                                    <div className="flex items-center justify-between text-xs"><span className="text-foreground/85">{t.metaFlex}</span><span className="font-semibold tabular-nums">${Number(minTopupFlex || minTopup).toLocaleString()}</span></div>
                                </div>
                            )}

                            <div>
                                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">{t.feeTitle}</p>
                                <table className="w-full text-sm" data-testid="platform-terms-fee-table">
                                    <thead>
                                        <tr className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-white/[0.07]">
                                            <th className="text-left py-1.5 font-medium">{t.spend}</th>
                                            <th className="text-right py-1.5 font-medium">{t.white}</th>
                                            <th className="text-right py-1.5 font-medium">{t.gray}</th>
                                            <th className="text-right py-1.5 font-medium">Casino</th>
                                            <th className="text-right py-1.5 font-medium">{t.cashback}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {TIERS.map((r) => (
                                            <tr key={r.spend} className="border-b border-white/[0.04] tabular-nums">
                                                <td className="py-1.5 font-mono text-xs">{r.spend}</td>
                                                <td className="py-1.5 text-right font-semibold">{r.white}%</td>
                                                <td className="py-1.5 text-right font-semibold">{r.gray}%</td>
                                                <td className="py-1.5 text-right font-semibold">{r.casino}%</td>
                                                <td className="py-1.5 text-right text-[#2FA57C]">{r.cashback || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {platform.slug === "meta" && <p className="text-xs text-muted-foreground mt-2" data-testid="platform-terms-casino">{t.casino}</p>}
                                <p className="text-xs text-foreground/70 mt-2">{t.example(Number(minTopup))}</p>
                            </div>

                            <ul className="grid sm:grid-cols-3 gap-2">
                                {t.perks.map((p) => (
                                    <li key={p} className="flex items-start gap-1.5 text-[11px] text-foreground/80"><BadgeCheck className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />{p}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="p-6 pt-4 flex flex-col sm:flex-row gap-2">
                            <Link to="/signup" className="flex-1" data-testid="platform-terms-signup">
                                <Button className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">{t.signup} <ArrowUpRight className="w-4 h-4 ml-1" /></Button>
                            </Link>
                            <Link to={localizedPath(language, "/calculator")} className="flex-1" data-testid="platform-terms-calculator">
                                <Button variant="outline" className="w-full h-11 border-white/10"><Calculator className="w-4 h-4 mr-2" /> {t.calc}</Button>
                            </Link>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
